// Shared reading of `customer.modules`, the object every product tier in the app is
// gated on (sc-9909).

/**
 * What `/user/:id` returns under `customer.modules`.
 *
 * The object mixes two kinds of value: a boolean per capability, and a
 * `<module>Price` number beside it holding what that module costs.
 *
 * ```json
 * { "apply": true, "applyWhite": false, "applyPrice": 250, "applyWhitePrice": 0 }
 * ```
 *
 * Nothing outside this file should read a flag off it directly - use
 * `isModuleEnabled`, which knows the difference between the two.
 */
export type CustomerModules = {
  apply?: boolean;
  applyWhite?: boolean;
  caseManagement?: boolean;
  creditReporter?: boolean;
  subOrganization?: boolean;
  whiteLabel?: boolean;
  sso?: boolean;
  applyPrice?: number;
  applyWhitePrice?: number;
  caseManagementPrice?: number;
  creditReporterPrice?: number;
  whiteLabelPrice?: number;
};

/**
 * Whether a module is switched on.
 *
 * Truthiness is not enough. `applyPrice` carries a dollar amount for every customer
 * whose Apply module is priced, and `!!250` reads that as an entitlement. The string
 * `"false"` read as on the same way in sc-9636. Only a real flag counts.
 */
export const isModuleEnabled = (value?: boolean | number | string | null): boolean => value === true

/** The Apply tiers, as the Apply tab and the configuration form both need them. */
export type ApplyAccess = {
  /** Whether the customer has Apply at all. Without it there is nothing to configure. */
  hasApplyAccess: boolean;
  isWhiteLabelEnabled: boolean;
};

/**
 * Apply is sold in tiers, and the server refuses any field belonging to a tier the
 * customer does not hold - `Cannot update the following fields when applyWhite is
 * disabled: primaryColor`. The form only shows a section when this says the customer
 * holds it, so the refusal never has a chance to happen.
 */
/** SSO signing keys belong to a parent whose SSO module is on. */
export const isSsoModuleEnabled = (modules?: CustomerModules | null): boolean => (
  isModuleEnabled(modules?.sso)
)

/**
 * Generate and rotate are Admin and Super only, and only when SSO is on.
 * @param profile stored user, including role and customer modules
 */
export const canManageSso = (profile?: {
  role?: { name?: string | null } | null
  customer?: { modules?: CustomerModules | null } | null
} | null): boolean => (
  (profile?.role?.name === 'Admin' || profile?.role?.name === 'Super')
  && isSsoModuleEnabled(profile?.customer?.modules)
)

export const getApplyAccess = (modules?: CustomerModules | null): ApplyAccess => ({
  hasApplyAccess: isModuleEnabled(modules?.apply) || isModuleEnabled(modules?.applyWhite),
  isWhiteLabelEnabled: isModuleEnabled(modules?.applyWhite),
})
